<template>
  <el-dialog
    :visible="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
    width="880px"
  >
    <div slot="title">
      {{ title }}
    </div>
    <div v-loading="loading">
      <el-form
        ref="docketForm"
        label-position="top"
        :model="docket"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              prop="type"
              label="Irány"
            >
              <el-select
                v-model="docket.type"
                style="width: 100%;"
                @change="onTypeChange"
              >
                <el-option
                  value="in"
                  label="Bejövő">Bejövő
                </el-option>
                <el-option
                  value="out"
                  label="Kimenő">Kimenő
                </el-option>
                <el-option
                  value="internal"
                  label="Belső">Belső
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="document_type"
              label="Irattípus"
            >
              <el-select
                v-model="docket.document_type"
                style="width: 100%;"
                value-key="id"
                filterable
                @change="onDocumentTypeChange"
              >
                <el-option
                  v-for="documentType in documentTypes"
                  :key="documentType.id"
                  :value="documentType"
                  :label="documentType.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row
          :gutter="20"
          style="display: flex; align-items: center"
        >
          <el-col :span="12">
            <el-form-item
              prop="from"
              label="Honnan"
            >
              <el-select
                :key="fromSelectKey"
                v-if="docket.type === 'in'"
                v-model="docket.from"
                filterable
                allow-create
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                placeholder="Írd be a partner nevét a kereséshez..."
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="partner in partners"
                  :key="partner.id + partner.type"
                  :value="partner"
                  :label="partner.company && partner.company.name || partner.name"
                />
              </el-select>
              <el-select
                v-else
                v-model="docket.from"
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="ownCompany in ownCompanies"
                  :key="ownCompany.id"
                  :value="ownCompany"
                  :label="ownCompany.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="to"
              label="Hova"
            >
              <el-select
                v-if="docket.type === 'out'"
                v-model="docket.to"
                filterable
                allow-create
                remote
                :remote-method="searchPartners"
                :loading="partnerLoading"
                placeholder="Írd be a partner nevét a kereséshez..."
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="partner in partners"
                  :key="partner.id + partner.type"
                  :value="partner"
                  :label="partner.company && partner.company.name || partner.name"
                />
              </el-select>
              <el-select
                v-else
                v-model="docket.to"
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="ownCompany in ownCompanies"
                  :key="ownCompany.id"
                  :value="ownCompany"
                  :label="ownCompany.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              prop="reference_number"
              label="Hivatkozási szám"
            >
              <el-input
                v-model="docket.reference_number"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="Dátum"
              prop="deadline"
            >
              <el-date-picker
                v-model="docket.deadline"
                type="date"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
                @input="datePickerKey++"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              label="Iktatókönyv"
              prop="docket_book"
            >
              <el-select
                v-model="docket.docket_book"
                default-first-option
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="docketBook in docketBooks"
                  :key="docketBook.id"
                  :value="docketBook"
                  :label="docketBook.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="related_docket"
              label="Előzmény"
            >
              <el-select
                v-model="docket.related_docket"
                v-loading="docketSearchLoading"
                style="width: 100%;"
                value-key="id"
                remote
                filterable
                placeholder="Írd be az iktatás számát a kereséshez..."
                :remote-method="searchDocket"
              >
                <el-option
                  v-for="relatedDocket in relatedDockets"
                  :key="relatedDocket.id"
                  :value="relatedDocket"
                  :label="`${relatedDocket.receipt_number} (${relatedDocket.document_type && relatedDocket.document_type.name})`"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              prop="responsible_user"
              label="Felelős"
            >
              <el-select
                v-model="docket.responsible_user"
                style="width: 100%;"
                value-key="id"
              >
                <el-option
                  v-for="user in users"
                  :key="user.id"
                  :value="user"
                  :label="`${user.last_name} ${user.first_name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              prop="responsible_user_deadline"
              label="Felelős határideje"
            >
              <el-date-picker
                v-model="docket.responsible_user_deadline"
                type="date"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
                @input="datePickerKey++"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <extra-field-form
        v-if="docket.document_type && docket.document_type.extra_fields && docket.document_type.extra_fields.length"
        ref="extraFieldForm"
        :docket-extra-field-values="docket.extra_fields"
        :document-type-fields="docket.document_type.extra_fields"
      />
      <files
        ref="files"
        :files="docket.files"
        :dialog-type="type"
        @fileChange="onFileChange"
        @add="onFileAdd"
        @fileDelete="onFileDelete"
      />
    </div>
    <div
      slot="footer"
      class="dialog-footer"
      style="display: flex; justify-content: space-between;"

    >
      <div>
        <el-alert
          v-if="buttonLoading && countAddedFiles > 2"
          type="info"
          :closable="false"
          style="margin-right: 10px; padding-left: 10px;"
        >
          <i
            class="el-icon-loading"
            style="font-weight: 600; font-size: 14px; margin-right: 5px;"></i> Iktatás feltöltése a fájlok méretétől függően
          akár néhány percig is eltarthat.
        </el-alert>
      </div>
      <el-button
        type="primary"
        :loading="buttonLoading"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>

  import ExtraFieldForm from './ExtraFieldsForm';
  import Files from './Files';

  export default {
    components: { ExtraFieldForm, Files },

    props: ['type', 'docketId'],
    data() {
      return {
        loading: false,
        fromSelectKey: 0,
        buttonLoading: false,
        partnerLoading: false,
        partners: [],
        docketBooks: [],
        documentTypes: [],
        relatedDockets: [],
        datePickerKey: 0,
        countAddedFiles: 0,
        fileIdsToDelete: [],
        docketSearchLoading: false,
        docket: {
          type: '',
          from: null,
          to: null,
          document_type: null,
          related_docket: {},
          responsible_user: {},
          extra_fields: {},
          docket_book: null,
          files: [],
        },
        rules: {
          type: [
            {
              required: true,
              message: 'Típus kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          from: [
            {
              required: true,
              message: 'Mező kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          to: [
            {
              required: true,
              message: 'Mező kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          docket_book: [
            {
              required: true,
              message: 'Mező kiválasztása kötelező',
              trigger: 'change'
            }
          ],
          document_type: [
            {
              required: true,
              message: 'Mező kiválasztása kötelező',
              trigger: 'change'
            }
          ],
        },
      };
    },
    computed: {
      title() {
        if (this.type === 'add') {
          return 'Új Iktatás';
        } else if (this.type === 'update') {
          return 'Iktatás módosítása';
        } else {
          return '';
        }
      },
      buttonText() {
        if (this.type === 'add') {
          return 'Hozzáadása';
        } else if (this.type === 'update') {
          return 'Módosítás';
        } else {
          return '';
        }
      },
      ownCompanies() {
        return this.$store.getters['ownCompany/ownCompanies'];
      },
      users() {
        return this.$store.getters['user/users'];
      }
    },
    async mounted() {
      this.loading = true;
      this.docketBooks = await this.$store.dispatch('docketBook/fetchAllWithoutPagination');
      this.documentTypes = await this.$store.dispatch('documentType/fetchAllWithoutPagination');
      const requests = [];
      if (!this.ownCompanies || !this.ownCompanies.length) {
        requests.push(this.$store.dispatch('ownCompany/fetchAll'));
      }
      if (!this.users || !this.users.length) {
        requests.push(this.$store.dispatch('user/fetchAll'));
      }
      await Promise.all(requests);
      if (this.docketId) {
        this.docket = await this.$store.dispatch('docket/fetch', this.docketId);
        if (this.docket.type === 'in') {
          this.partners = [this.docket.from];
        } else {
          this.partners = [this.docket.to];
        }
        this.fromSelectKey++;
      }
      this.loading = false;
    },
    methods: {
      async searchDocket(val) {
        this.docketSearchLoading = true;
        try {
          this.relatedDockets = await axios.get(`api/dockets/search/${val}`);
        } catch (e) {}
        this.docketSearchLoading = false;
      },
      async searchPartners(query) {
        this.partnerLoading = true;
        if (!query) {
          this.partners = [];
        } else {
          const queryString = new URLSearchParams({
            name: query,
          });
          this.partners = await axios.get('api/dockets/partners' + '?' + queryString.toString());
        }
        this.partnerLoading = false;
      },
      handleClose() {
        this.$confirm('Biztosan elveted a módosításokat?', {
          confirmButtonText: 'Igen',
          type: 'warning',
        })
        .then(() => {
          this.$router.replace('/docket/dockets');
        })
        .catch(() => {
        });
      },
      async store() {
        if (this.checkFormsValid()) {
          this.buttonLoading = true;
          this.docket.extra_fields = this.$refs['extraFieldForm'] && this.$refs['extraFieldForm'].extraFieldValues;
          try {
            if (this.type === 'add') {
              const response = await this.$store.dispatch('docket/create', this.docket);
              await this.uploadFiles(response.id);
              this.$messageWrapper.success('Iktatás sikeresen hozzáadva');
            } else if (this.type === 'update') {
              const response = await this.$store.dispatch('docket/update', this.docket);
              await this.uploadFiles(response.id);
              await this.deleteFiles();
              this.$messageWrapper.success('Iktatás sikeresen mòdosítva');
            }
            this.$store.dispatch('docket/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/docket/dockets');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      },
      checkFormsValid() {
        let allValid = false;
        this.$refs.docketForm.validate(async valid => {
          if (valid) {
            if (this.docket.document_type.extra_fields.length) {
              this.$refs.extraFieldForm.$refs.extraFieldForm.validate(async valid => {
                if (valid) {
                  allValid = true;
                } else {
                  allValid = false;
                }
              });
            } else {
              allValid = true;
            }
          }
        });
        return allValid;
      },
      onDocumentTypeChange() {
        this.$refs.extraFieldForm && this.$refs.extraFieldForm.$refs && this.$refs.extraFieldForm.$refs.extraFieldForm.clearValidate();
        this.docket.extra_fields = {};
      },
      onTypeChange(type) {
        if(type === 'in') {
          this.docket.from = null;
          this.docket.to = this.ownCompanies[0];
        } else if(type === 'out') {
          this.docket.from = this.ownCompanies[0];
          this.docket.to = null;
        } else {
          this.docket.from = this.ownCompanies[0];
          this.docket.to = this.ownCompanies[0];
        }
        this.$refs.docketForm.clearValidate();
      },
      onFileAdd(files) {
        this.docket.files = this.docket.files.concat(files);
        this.countAddedFiles += files.length;
      },
      onFileChange(files) {
        this.docket.files = files;
      },
      onFileDelete(file) {
        if (file.id) {
          this.fileIdsToDelete.push(file.id);
        } else {
          this.countAddedFiles--;
        }
      },
      async uploadFiles(docketId) {
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        };
        if (this.docket.files.length) {
          for (let [idx, file] of this.docket.files.entries()) {
            let formData = new FormData();
            formData.append('docket_id', docketId);
            if (!file.id) {
              formData.append('file', file.source);
              await axios.post('api/dockets/upload-file', formData, config);
            }
          }
        }
      },
      async deleteFiles() {
        if (this.fileIdsToDelete.length) {
          return await axios.post('api/dockets/delete-files', { ids: this.fileIdsToDelete });
        }
      }
    }
  }
</script>

